'use client'

import { Fragment, useId, useState } from 'react'
import { normalizeSubdivision, shippingSubdivisions, type CheckoutAddress } from '@sifly/shared-utils'

/**
 * The shipping address on /checkout and on a quote's payment section. Ours,
 * not Stripe's Address Element: the Session then collects no address, so a
 * Shipping ↔ Pickup switch updates it in place instead of rebuilding the
 * payment form, and the address survives a Card ↔ Bank switch (the page
 * holds it, not the SDK instance). The page hands it to Stripe at confirm,
 * where it lands on the payment the dealer sees.
 *
 * Used whatever the payment method, so card, bank and the manual methods
 * collect the address in one layout; the cart drawer's manual form
 * (CustomerDetailsForm) shares its state/province list. Same box as
 * ContactFields beside it.
 */

export type AddressDraft = { line1: string; line2: string; city: string; state: string; postalCode: string; country: 'US' | 'CA' }

export const EMPTY_ADDRESS: AddressDraft = { line1: '', line2: '', city: '', state: '', postalCode: '', country: 'US' }

type Field = 'line1' | 'city' | 'state' | 'postalCode'

const INPUT_CLASS =
  'w-full min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-base text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]'
const LABEL_CLASS = 'block text-sm font-medium text-[var(--color-text-secondary)] mb-1'
const ERROR_CLASS = 'mt-1 text-xs text-[var(--color-error-strong)]'

const US_ZIP = /^\d{5}(-?\d{4})?$/
const CA_POSTAL = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/

function addressErrors(d: AddressDraft): Partial<Record<Field, string>> {
  const errors: Partial<Record<Field, string>> = {}
  if (!d.line1.trim()) errors.line1 = 'A street address is required.'
  if (!d.city.trim()) errors.city = 'A city is required.'
  if (!normalizeSubdivision(d.country, d.state)) errors.state = d.country === 'CA' ? 'Choose a province.' : 'Choose a state.'
  const postal = d.postalCode.trim()
  if (!postal) errors.postalCode = d.country === 'CA' ? 'A postal code is required.' : 'A ZIP code is required.'
  else if (d.country === 'US' && !US_ZIP.test(postal)) errors.postalCode = 'Enter a 5-digit ZIP code.'
  else if (d.country === 'CA' && !CA_POSTAL.test(postal)) errors.postalCode = 'Enter a postal code like A1A 1A1.'
  return errors
}

/** The draft as the checkout input carries it, or null while incomplete. */
export function completeAddress(d: AddressDraft): CheckoutAddress | null {
  if (Object.keys(addressErrors(d)).length > 0) return null
  return {
    line1: d.line1.trim(),
    ...(d.line2.trim() ? { line2: d.line2.trim() } : {}),
    city: d.city.trim(),
    state: normalizeSubdivision(d.country, d.state) ?? d.state.trim(),
    postalCode: d.postalCode.trim().toUpperCase(),
    country: d.country,
  }
}

export function draftFromAddress(a: CheckoutAddress): AddressDraft {
  const country = a.country === 'CA' ? 'CA' : 'US'
  return { line1: a.line1, line2: a.line2 ?? '', city: a.city, state: normalizeSubdivision(country, a.state) ?? '', postalCode: a.postalCode, country }
}

/** Stripe.js's contact shape, for `confirm({ shippingAddress })`. */
export function stripeShippingContact(name: string, a: CheckoutAddress) {
  return {
    name,
    address: { country: a.country, line1: a.line1, line2: a.line2 ?? null, city: a.city, state: a.state, postal_code: a.postalCode },
  }
}

const SUBDIVISION_GROUPS = [
  ['state', null],
  ['territory', 'US territories'],
  ['military', 'Military (APO/FPO)'],
] as const

/** The `<option>`s for a state/province `<select>`: a blank prompt, then the
 *  country's list with codes as values and names as labels (browser autofill
 *  matches either). Shared with the manual-payment form in the cart drawer. */
export function SubdivisionOptions({ country }: { country: string }) {
  return (
    <>
      <option value="">{country === 'CA' ? 'Province' : 'State'}</option>
      {SUBDIVISION_GROUPS.map(([group, label]) => {
        const items = shippingSubdivisions(country).filter((s) => s.group === group)
        const options = items.map((s) => (
          <option key={s.code} value={s.code}>{group === 'military' ? `${s.name} (${s.code})` : s.name}</option>
        ))
        // A plain list for Canada; the US gets headed groups after the states.
        return items.length === 0 ? null : label ? <optgroup key={group} label={label}>{options}</optgroup> : <Fragment key={group}>{options}</Fragment>
      })}
    </>
  )
}

export default function AddressFields({
  value,
  onChange,
  showErrors,
}: {
  value: AddressDraft
  onChange: (v: AddressDraft) => void
  /** Show every field's message (the buyer tried to pay); otherwise a field's
   *  message waits until the buyer has left it. */
  showErrors: boolean
}) {
  const id = useId()
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({})
  const errors = addressErrors(value)
  const shown = (f: Field) => (showErrors || touched[f] ? errors[f] : undefined)
  const blur = (f: Field) => () => setTouched((t) => (t[f] ? t : { ...t, [f]: true }))
  const set = (patch: Partial<AddressDraft>) => onChange({ ...value, ...patch })

  const field = (f: Field, label: string, autoComplete: string, extra: { inputMode?: 'text' | 'numeric' } = {}) => {
    const fid = `${id}-${f}`
    const err = shown(f)
    return (
      <div>
        <label htmlFor={fid} className={LABEL_CLASS}>{label}</label>
        <input
          id={fid}
          type="text"
          autoComplete={autoComplete}
          value={value[f]}
          onChange={(e) => set({ [f]: e.target.value } as Partial<AddressDraft>)}
          onBlur={blur(f)}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${fid}-error` : undefined}
          className={INPUT_CLASS}
          {...extra}
        />
        {err && <p id={`${fid}-error`} className={ERROR_CLASS}>{err}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {field('line1', 'Street address', 'shipping address-line1')}
      <div>
        <label htmlFor={`${id}-line2`} className={LABEL_CLASS}>Apartment, suite, etc. (optional)</label>
        <input
          id={`${id}-line2`}
          type="text"
          autoComplete="shipping address-line2"
          value={value.line2}
          onChange={(e) => set({ line2: e.target.value })}
          className={INPUT_CLASS}
        />
      </div>
      {field('city', 'City', 'shipping address-level2')}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${id}-state`} className={LABEL_CLASS}>{value.country === 'CA' ? 'Province' : 'State'}</label>
          <select
            id={`${id}-state`}
            autoComplete="shipping address-level1"
            value={normalizeSubdivision(value.country, value.state) ?? ''}
            onChange={(e) => set({ state: e.target.value })}
            onBlur={blur('state')}
            aria-invalid={shown('state') ? true : undefined}
            aria-describedby={shown('state') ? `${id}-state-error` : undefined}
            className={INPUT_CLASS}
          >
            <SubdivisionOptions country={value.country} />
          </select>
          {shown('state') && <p id={`${id}-state-error`} className={ERROR_CLASS}>{shown('state')}</p>}
        </div>
        {field('postalCode', value.country === 'CA' ? 'Postal code' : 'ZIP code', 'shipping postal-code', value.country === 'US' ? { inputMode: 'numeric' } : {})}
      </div>
      <div>
        <label htmlFor={`${id}-country`} className={LABEL_CLASS}>Country</label>
        <select
          id={`${id}-country`}
          autoComplete="shipping country"
          value={value.country}
          onChange={(e) => {
            const country = e.target.value === 'CA' ? 'CA' : 'US'
            // A state is not a province: clear one that the new country lacks.
            set({ country, state: normalizeSubdivision(country, value.state) ?? '' })
          }}
          className={INPUT_CLASS}
        >
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </div>
    </div>
  )
}
